import React, { useCallback, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { bandChipSx, bandTone, normalizeSortOrder } from './assessmentUiUtils';
import { getUniqueScoreValues, scoreToneFromValue, scoreToggleButtonSx } from './scoreUtils';

function pickBandLabel(total, bandsSorted) {
  if (!Number.isFinite(Number(total))) return null;
  const t = Number(total);

  for (const b of bandsSorted || []) {
    if (!b) continue;
    const mn = b.min_score === null || b.min_score === undefined ? null : Number(b.min_score);
    const mx = b.max_score === null || b.max_score === undefined ? null : Number(b.max_score);

    const geMin = mn === null ? true : t >= mn;
    const leMax = mx === null ? true : t <= mx;
    if (geMin && leMax) return b.label || null;
  }
  return null;
}

function studentLabel(st) {
  if (!st) return '';
  const no = String(st.student_no ?? st.id ?? '');
  const name = `${String(st.first_name || '')} ${String(st.last_name || '')}`.trim();
  return `${no} — ${name}`.trim();
}

export default function AssessmentTableViewV2({
  students,
  sortedCriteria,
  focusedCriterionId,
  itemsByKey,
  onSetScore,
  readOnly,
  isSavingScore,
  bands,
  forceMobileLayout,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const useMobileLayout = !!forceMobileLayout || isMobile;

  const tableGridSx = useMemo(
    () => ({
      tableLayout: 'fixed',
      width: 'max-content',
      minWidth: '100%',

      // Modern, clean grid lines (vertical + horizontal)
      '& .MuiTableRow-root > .MuiTableCell-root': {
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
      '& .MuiTableRow-root > .MuiTableCell-root:not(:last-of-type)': {
        borderRight: '1px solid',
        borderColor: 'divider',
      },
      '& .MuiTableBody-root .MuiTableRow-root:last-child > .MuiTableCell-root': {
        borderBottom: 'none',
      },

      // Header background should be distinct from zebra rows
      '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.08)',
      },

      // Subtle zebra striping to improve scanning across many columns
      '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[50] : 'rgba(255,255,255,0.04)'),
      },
      '& .MuiTableBody-root .MuiTableRow-root:hover': {
        backgroundColor: 'action.hover',
      },
    }),
    []
  );

  const allCriteria = useMemo(() => (Array.isArray(sortedCriteria) ? sortedCriteria : []), [sortedCriteria]);
  const criteriaToShow = useMemo(() => {
    if (!focusedCriterionId) return allCriteria;
    return allCriteria.filter((c) => String(c?.id) === String(focusedCriterionId));
  }, [allCriteria, focusedCriterionId]);

  const bandsSorted = useMemo(
    () => (bands || []).slice().sort((a, b) => normalizeSortOrder(a?.sort_order) - normalizeSortOrder(b?.sort_order)),
    [bands]
  );

  const totalByStudentId = useMemo(() => {
    const totals = {};
    (students || []).forEach((st) => {
      if (!st || st.id == null) return;
      let total = 0;
      (allCriteria || []).forEach((c) => {
        if (!c || c.id == null) return;
        const key = `${st.id}:${c.id}`;
        const score = itemsByKey?.[String(key)]?.score;
        if (score === null || score === undefined || score === '') return;
        const n = Number(score);
        if (!Number.isFinite(n)) return;
        total += n;
      });
      totals[String(st.id)] = total;
    });
    return totals;
  }, [students, allCriteria, itemsByKey]);

  const missingByStudentId = useMemo(() => {
    const out = {};
    const cids = (allCriteria || []).filter((c) => c && c.id != null).map((c) => String(c.id));
    (students || []).forEach((st) => {
      if (!st || st.id == null) return;
      const sid = String(st.id);
      let missing = 0;
      for (const cid of cids) {
        const k = `${sid}:${cid}`;
        const score = itemsByKey?.[String(k)]?.score;
        const hasScore = score !== null && score !== undefined && score !== '' && Number.isFinite(Number(score));
        if (!hasScore) missing += 1;
      }
      out[sid] = missing;
    });
    return out;
  }, [students, allCriteria, itemsByKey]);

  const [selectedStudentId, setSelectedStudentId] = useState(() => {
    const first = (students || [])[0];
    return first?.id ?? null;
  });

  const selectedStudent = useMemo(() => {
    if (!selectedStudentId) return (students || [])[0] || null;
    return (students || []).find((s) => String(s?.id) === String(selectedStudentId)) || null;
  }, [students, selectedStudentId]);

  const selectNextMissing = useCallback(() => {
    const list = Array.isArray(students) ? students : [];
    if (!list.length) return;

    const startIdx = Math.max(0, list.findIndex((s) => String(s?.id) === String(selectedStudent?.id)));
    const order = [...list.slice(startIdx + 1), ...list.slice(0, startIdx + 1)];

    const found = order.find((s) => {
      const sid = String(s?.id);
      return Number(missingByStudentId[sid] || 0) > 0;
    });

    if (found?.id != null) setSelectedStudentId(found.id);
  }, [students, selectedStudent?.id, missingByStudentId]);

  const stickyLeftHeadSx = useMemo(
    () => ({
      position: 'sticky',
      left: 0,
      zIndex: 4,
      backgroundColor: (theme) =>
        theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.08)',
      borderRight: '1px solid',
      borderColor: 'divider',
    }),
    []
  );
  const stickyLeftBodySx = useMemo(
    () => ({
      position: 'sticky',
      left: 0,
      zIndex: 3,
      backgroundColor: 'background.paper',
      borderRight: '1px solid',
      borderColor: 'divider',
    }),
    []
  );

  const stickyRightGeneralHeadSx = useMemo(
    () => ({
      position: 'sticky',
      right: 0,
      zIndex: 4,
      backgroundColor: (theme) =>
        theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.08)',
      borderLeft: '1px solid',
      borderColor: 'divider',
    }),
    []
  );
  const stickyRightTotalHeadSx = useMemo(
    () => ({
      position: 'sticky',
      right: 140,
      zIndex: 4,
      backgroundColor: (theme) =>
        theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.08)',
      borderLeft: '1px solid',
      borderColor: 'divider',
    }),
    []
  );
  const stickyRightGeneralBodySx = useMemo(
    () => ({
      position: 'sticky',
      right: 0,
      zIndex: 2,
      backgroundColor: 'inherit',
      borderLeft: '1px solid',
      borderColor: 'divider',
    }),
    []
  );
  const stickyRightTotalBodySx = useMemo(
    () => ({
      position: 'sticky',
      right: 140,
      zIndex: 2,
      backgroundColor: 'inherit',
      borderLeft: '1px solid',
      borderColor: 'divider',
    }),
    []
  );

  if (useMobileLayout) {
    const sid = selectedStudent?.id;
    const total = totalByStudentId[String(sid)] ?? 0;
    const bandLabel = pickBandLabel(total, bandsSorted);
    const tone = bandTone(bandLabel);

    const allCount = (allCriteria || []).length;
    const missing = Number(missingByStudentId[String(sid)] || 0);
    const recorded = Math.max(0, allCount - missing);

    return (
      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            size="small"
            options={students || []}
            value={selectedStudent}
            getOptionLabel={(opt) => studentLabel(opt)}
            onChange={(_, v) => setSelectedStudentId(v?.id ?? null)}
            sx={{ minWidth: 220, flex: 1 }}
            renderInput={(params) => <TextField {...params} label="Öğrenci" placeholder="Ara..." />}
          />

          <Button
            size="small"
            variant="outlined"
            onClick={selectNextMissing}
            disabled={!students || students.length === 0}
          >
            Eksik → Sonraki
          </Button>
        </Box>

        {selectedStudent ? (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip size="small" label={`Toplam: ${String(total)}`} />
            {bandLabel ? (
              <Chip size="small" variant="outlined" color={tone} sx={bandChipSx(tone)} label={String(bandLabel)} />
            ) : null}
            <Chip size="small" label={`Kayıt: ${recorded}/${allCount}`} />
            <Chip size="small" label={`Eksik: ${missing}`} color={missing === 0 ? 'success' : 'warning'} />
          </Box>
        ) : null}

        <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(criteriaToShow || []).map((c) => {
            const key = `${sid}:${c?.id}`;
            const item = itemsByKey?.[String(key)] || {};

            const scoreValues = Array.isArray(c?.score_values) ? c.score_values : [0, 1, 2, 3];
            const uniqScoreValues = getUniqueScoreValues(scoreValues, [0, 1, 2, 3]);
            const currentScore = Number.isFinite(Number(item?.score)) ? Number(item.score) : null;
            const isSavingThis = isSavingScore ? !!isSavingScore(sid, c?.id) : false;

            const title = c?.section_title ? `${String(c.section_title)} — ${String(c?.name || '')}` : String(c?.name || '');

            return (
              <Paper key={String(c?.id)} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  {title}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={currentScore}
                    onChange={(_, v) => onSetScore && onSetScore(sid, c?.id, v)}
                    disabled={readOnly || isSavingThis}
                    sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1, minWidth: 36 } }}
                  >
                    {uniqScoreValues.map((v) => (
                      <ToggleButton
                        key={String(v)}
                        value={Number(v)}
                        sx={scoreToggleButtonSx(scoreToneFromValue(v, uniqScoreValues))}
                      >
                        {String(v)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>

                  <Typography variant="caption" color="text.secondary">
                    {currentScore === null ? 'Seçilmedi' : `Seçildi: ${String(currentScore)}`}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Paper>
    );
  }

  // Desktop/tablet matrix view
  return (
    <TableContainer sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Table size="small" stickyHeader sx={tableGridSx}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ width: 110, minWidth: 110, maxWidth: 110, fontWeight: 800, ...stickyLeftHeadSx }}
            >
              Öğrenci
            </TableCell>

            {(criteriaToShow || []).map((c) => {
              const label = c?.section_title ? `${String(c.section_title)} — ${String(c?.name || '')}` : String(c?.name || '');
              const scoreValues = Array.isArray(c?.score_values) ? c.score_values : [0, 1, 2, 3];
              const uniqScoreValues = getUniqueScoreValues(scoreValues, [0, 1, 2, 3]);
              const optionCount = Math.max(2, Number(uniqScoreValues?.length || 0));
              const criterionColWidth = Math.min(240, Math.max(120, optionCount * 36 + 36));
              return (
                <TableCell
                  key={String(c?.id)}
                  sx={{ width: criterionColWidth, minWidth: criterionColWidth, maxWidth: criterionColWidth, verticalAlign: 'bottom' }}
                >
                  <Tooltip title={label} placement="top" arrow>
                    <Box>
                      {c?.section_title ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                          {String(c.section_title)}
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {String(c?.name || '')}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
              );
            })}

            <TableCell
              sx={{ width: 90, minWidth: 90, maxWidth: 90, fontWeight: 800, textAlign: 'center', ...stickyRightTotalHeadSx }}
            >
              Toplam
            </TableCell>
            <TableCell
              sx={{ width: 140, minWidth: 140, maxWidth: 140, fontWeight: 800, ...stickyRightGeneralHeadSx }}
            >
              Genel
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {(students || []).map((st) => {
            const sid = st?.id;
            const total = totalByStudentId[String(sid)] ?? 0;
            const bandLabel = pickBandLabel(total, bandsSorted);
            const tone = bandTone(bandLabel);

            return (
              <TableRow key={String(sid)} hover>
                <TableCell sx={{ ...stickyLeftBodySx, width: 140, minWidth: 140, maxWidth: 140 }}>
                  <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }} noWrap>
                    {String(st?.student_no ?? st?.id ?? '')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {String(st?.first_name || '')} {String(st?.last_name || '')}
                  </Typography>
                </TableCell>

                {(criteriaToShow || []).map((c) => {
                  const key = `${sid}:${c?.id}`;
                  const item = itemsByKey?.[String(key)] || {};

                  const scoreValues = Array.isArray(c?.score_values) ? c.score_values : [0, 1, 2, 3];
                  const uniqScoreValues = getUniqueScoreValues(scoreValues, [0, 1, 2, 3]);
                  const optionCount = Math.max(2, Number(uniqScoreValues?.length || 0));
                  const criterionColWidth = Math.min(240, Math.max(120, optionCount * 36 + 36));
                  const currentScore = Number.isFinite(Number(item?.score)) ? Number(item.score) : null;
                  const isSavingThis = isSavingScore ? !!isSavingScore(sid, c?.id) : false;

                  return (
                    <TableCell
                      key={String(c?.id)}
                      sx={{ width: criterionColWidth, minWidth: criterionColWidth, maxWidth: criterionColWidth, py: 0.75 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={currentScore}
                          onChange={(_, v) => onSetScore && onSetScore(sid, c?.id, v)}
                          disabled={readOnly || isSavingThis}
                          sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1, minWidth: 32 } }}
                        >
                          {uniqScoreValues.map((v) => (
                            <ToggleButton
                              key={String(v)}
                              value={Number(v)}
                              sx={scoreToggleButtonSx(scoreToneFromValue(v, uniqScoreValues))}
                            >
                              {String(v)}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                    </TableCell>
                  );
                })}

                <TableCell sx={{ width: 90, minWidth: 90, maxWidth: 90, textAlign: 'center', ...stickyRightTotalBodySx }}>
                  <Chip size="small" label={String(total)} />
                </TableCell>

                <TableCell sx={{ width: 140, minWidth: 140, maxWidth: 140, ...stickyRightGeneralBodySx }}>
                  {bandLabel ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={tone}
                      sx={bandChipSx(tone)}
                      label={String(bandLabel)}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
